import React, { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  Alert
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { forgotPassword } from '../../../redux/auth/authSlice';
import { API_CALL_STATUS } from '../../../assets/constants/constants';
import Loader from '../../loader/Loader';
import { useNotification } from '../../shared/NotificationProvider';
import FormInput from '../shared/FormInput'; // Use common FormInput

const ForgetPasswordForm = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const apiCallStatus = useSelector(state => state.auth.forgotStatus);
  const forgotPasswordRes = useSelector(state => state.auth.forgotPasswordRes);
  const notify = useNotification();
  const [emailSent, setEmailSent] = useState(false);

  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, formState: { isSubmitting }, watch } = methods;

  const email = watch('email', '');

  const onSubmit = async (data) => {
    try {
      const res = await dispatch(forgotPassword({ email: data.email }));

      if (res.payload?.status === 'success') {
        setEmailSent(true);
        notify.success(res.payload.message || "Password reset link sent successfully!");
        
        // Reset form after successful submission
        setTimeout(() => {
          methods.reset();
        }, 2000);
      } else {
        notify.error(res.payload?.message || "Failed to send reset link. Please try again.");
      }
    } catch (error) {
      notify.error("An error occurred. Please try again later.");
    }
  };

  const handleResendEmail = () => {
    if (email) {
      onSubmit({ email });
    }
  };

  return (
    <FormProvider {...methods}>
      {apiCallStatus === API_CALL_STATUS.LOADING && <Loader />}

      <Box 
        component="form" 
        onSubmit={handleSubmit(onSubmit)} 
        sx={{ 
          mt: 3,
          width: '100%',
        }}
      >
        <Stack spacing={3}>
          {/* Success Message */}
          {emailSent && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Alert 
                severity="success" 
                icon={<CheckCircleIcon />}
                sx={{
                  background: 'rgba(0, 255, 136, 0.1)',
                  border: '1px solid rgba(0, 255, 136, 0.3)',
                  color: '#00ff88',
                  borderRadius: '12px',
                  '& .MuiAlert-icon': {
                    color: '#00ff88',
                  },
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Reset link sent successfully!
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                  Check your inbox for the password reset instructions.
                </Typography>
              </Alert>
            </motion.div>
          )}
          {/* Email Field */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <FormInput
              name="email"
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              startIcon={<EmailIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />}
              validationRules={{
                required: "Email address is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Please enter a valid email address"
                }
              }}
            />
          </motion.div>

          {/* Send Reset Link Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              variant="gradient"
              type="submit"
              disabled={isSubmitting || apiCallStatus === 'loading'}
              fullWidth
              startIcon={!emailSent ? <SendIcon /> : <CheckCircleIcon />}
              sx={{
                py: 1.8,
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '12px',
                background: emailSent 
                  ? 'linear-gradient(45deg, #00cc6d, #0077cc)' 
                  : 'linear-gradient(45deg, #00ff88, #0099ff)',
                color: '#000814',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s ease',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 30px rgba(0, 255, 136, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.5)',
                },
              }}
            >
              {isSubmitting || apiCallStatus === 'loading' 
                ? 'Sending...' 
                : emailSent 
                  ? 'Link Sent!' 
                  : 'Send Reset Link'}
            </Button>
          </motion.div>

          {/* Resend Option */}
          {emailSent && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Typography 
                variant="body2" 
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textAlign: 'center',
                  mt: 1,
                }}
              >
                Didn't receive the email?{' '}
                <Button
                  onClick={handleResendEmail}
                  disabled={isSubmitting || apiCallStatus === 'loading'}
                  sx={{
                    color: '#00ff88',
                    fontWeight: 600,
                    textTransform: 'none',
                    padding: 0,
                    minWidth: 'auto',
                    fontSize: 'inherit',
                    '&:hover': {
                      background: 'transparent',
                      color: '#66ffb3',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Resend link
                </Button>
              </Typography>
            </motion.div>
          )}

          {/* Additional Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Alert 
              severity="info"
              sx={{
                background: 'rgba(0, 153, 255, 0.1)',
                border: '1px solid rgba(0, 153, 255, 0.3)',
                color: '#0099ff',
                borderRadius: '12px',
                mt: 2,
                '& .MuiAlert-icon': {
                  color: '#0099ff',
                },
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
                <strong>Note:</strong> The reset link will expire in 1 hour. 
                If you don't see the email, check your spam folder.
              </Typography>
            </Alert>
          </motion.div>

          {/* Tips */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: 2,
                mt: 2,
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  display: 'block',
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                🔒 Security Tips:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <li>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Use the link within 1 hour
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Create a strong new password
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption" sx={{ color: 'rgba( 255, 255, 255, 0.6)' }}>
                    Don't share your reset link with anyone
                  </Typography>
                </li>
              </Box>
            </Box>
          </motion.div>
        </Stack>
      </Box>
    </FormProvider>
  );
};

export default ForgetPasswordForm;